import CTALink from "@/components/CTA/variants/CtaLink/client";
import {
  HOMEPAGE_HERO_DARK_BG,
  HOMEPAGE_HERO_LIGHT_BG,
} from "@/components/Homepage/HomepageHero/constants";
import type { HomepageHeroClientProps } from "@/components/Homepage/HomepageHero/types";
import HomepageHeroBackgroundImage from "@/components/Homepage/HomepageHero/components/HomepageHeroBackgroundImage";
import ProductCardList from "@/components/ProductCardList";
import { FormatSubType, FormatType } from "@/constants/tagular/main";
import { cn } from "@/lib/utils";
import Tags from "@/components/Tags";
import useTranslations from "@/i18n/useTranslations";

export default function BaseHomepageHeroSpotlight({
  title,
  descriptionItems,
  primaryCta,
  secondaryCta,
  desktopImage,
  tabletImage,
  mobileImage,
  theme = "dark",
  backgroundColor,
  products,
  eventing,
}: HomepageHeroClientProps) {
  const tCommon = useTranslations("Common");
  const hasImages = desktopImage || tabletImage || mobileImage;
  const defaultBgColor =
    theme === "dark" ? HOMEPAGE_HERO_DARK_BG : HOMEPAGE_HERO_LIGHT_BG;
  const bgColor =
    !hasImages && backgroundColor ? backgroundColor : defaultBgColor;
  return (
    <div
      className={cn(
        "relative flex flex-col justify-center items-center overflow-hidden",
        !backgroundColor && bgColor,
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {/* Background Images */}
      {(desktopImage || tabletImage || mobileImage) && (
        <>
          {mobileImage && (
            <HomepageHeroBackgroundImage
              imageSrc={mobileImage}
              screenSize="mobile"
              theme={theme}
            />
          )}
          {tabletImage && (
            <HomepageHeroBackgroundImage
              imageSrc={tabletImage}
              screenSize="tablet"
              theme={theme}
            />
          )}
          {desktopImage && (
            <HomepageHeroBackgroundImage
              imageSrc={desktopImage}
              screenSize="desktop"
              theme={theme}
            />
          )}
        </>
      )}

      <div className="container xl:max-w-screen-2xl py-20 pb-48 px-20">
        <div className="flex flex-col xl:flex-row gap-8 xl:gap-12 items-start">
          {/* Left Side: Content */}
          <div className="flex flex-col items-start xl:w-1/2 xl:max-w-xl self-center">
            <div className="mb-3">
              <Tags
                variant="badges"
                tags={[{ text: tCommon("newBadge"), color: "orange" }]}
              />
            </div>

            <h1 className="elm-d3 font-black italic mb-4 pt-0">
              {title?.split(" ").map((word, index) => (
                <span
                  key={index}
                  className={
                    word.toUpperCase() === "NEW"
                      ? "text-[#D74000]"
                      : "text-white"
                  }
                >
                  {word}{" "}
                </span>
              ))}
            </h1>

            {descriptionItems && descriptionItems.length > 0 && (
              <p className="text-white text-lg mb-6">{descriptionItems[0]}</p>
            )}

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 w-full sm:w-auto">
              {primaryCta && (
                <CTALink
                  type="brand"
                  url={primaryCta.url}
                  label={primaryCta.label}
                  html_id={eventing?.primaryCtaHtmlId}
                  name={eventing?.primaryCtaName}
                  location={eventing?.sectionLocation ?? "hero-spotlight"}
                  size="lg"
                  radius="none"
                  className="rounded-full px-6 w-full sm:w-auto text-center"
                />
              )}
              {secondaryCta && (
                <CTALink
                  type="brandOutline"
                  url={secondaryCta.url}
                  label={secondaryCta.label}
                  html_id={eventing?.secondaryCtaHtmlId}
                  name={eventing?.secondaryCtaName}
                  location={eventing?.sectionLocation ?? "hero-spotlight"}
                  size="lg"
                  radius="none"
                  className="rounded-full px-6 w-full sm:w-auto text-center"
                />
              )}
            </div>
          </div>

          {/* Right Side: Product Cards */}
          <div className="w-full xl:w-1/2 xl:flex-1">
            <ProductCardList
              className="flex flex-wrap sm:flex-nowrap gap-8 py-0"
              products={products}
              shouldScroll={false}
              cardFormatType={FormatType.Hero}
              cardFormatSubType={FormatSubType.HeroCard}
              location={eventing?.sectionLocation ?? "hero-spotlight"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
